/**
 * API Client for Admin App
 * 
 * Handles all API requests to the backend API server
 */
import { getAuthToken } from '@/hooks/use-auth';

export interface ApiClient {
  baseUrl: string;
  request<T>(endpoint: string, options?: RequestInit): Promise<T>;
}

export function createApiClient(baseUrl: string): ApiClient {
  return {
    baseUrl,
    async request<T>(endpoint: string, options?: RequestInit): Promise<T> {
      try {
        // Get auth token
        const token = await getAuthToken();
        
        // Check if token exists before making request
        if (!token) {
          console.error('API Client: No token available');
          throw new Error('Unauthorized: No authentication token found. Please sign in.');
        }
        
        console.log('API Client: Making request to', `${baseUrl}${endpoint}`, 'with token:', token.substring(0, 20) + '...');
        
        // Check if body is FormData - if so, don't set Content-Type (browser will set it with boundary)
        const isFormData = options?.body instanceof FormData;
        const headers: HeadersInit = {
          Authorization: `Bearer ${token}`,
          ...options?.headers,
        };
        
        // Only set Content-Type for non-FormData requests
        if (!isFormData) {
          headers['Content-Type'] = 'application/json';
        }
        
        const response = await fetch(`${baseUrl}${endpoint}`, {
          ...options,
          headers,
          credentials: 'include', // Include cookies for auth
        });

        console.log('API Client: Response status', response.status, 'for', endpoint);

        if (!response.ok) {
          // Handle 401 Unauthorized - throw error but don't redirect here
          // Let React Query and components handle the error
          if (response.status === 401) {
            const errorData = await response.json().catch(() => ({ message: 'Unauthorized' }));
            console.error('API Client: 401 Unauthorized for', endpoint, errorData);
            const error = new Error(errorData.message || 'Unauthorized: Your session may have expired. Please sign in again.');
            (error as any).status = 401;
            throw error;
          }
          
          // Try to get error message from response
          let errorMessage = response.statusText;
          try {
            const errorData = await response.json();
            errorMessage = errorData.error || errorData.message || errorMessage;
            console.error('API Client: Error response:', {
              status: response.status,
              statusText: response.statusText,
              endpoint,
              error: errorData,
            });
          } catch (e) {
            console.error('API Client: Failed to parse error response:', {
              status: response.status,
              statusText: response.statusText,
              endpoint,
            });
          }
          
          throw new Error(errorMessage || `API request failed: ${response.statusText}`);
        }

        return response.json();
      } catch (error) {
        // Handle network errors (Failed to fetch)
        if (error instanceof TypeError && error.message === 'Failed to fetch') {
          console.error('API Client: Network error - Failed to fetch', {
            baseUrl,
            endpoint,
            fullUrl: `${baseUrl}${endpoint}`,
          });
          throw new Error(`Không thể kết nối đến API server. Vui lòng kiểm tra API server có đang chạy tại ${baseUrl} không.`);
        }
        
        // Re-throw other errors
        throw error;
      }
    },
  };
}

// Get API client from DI container
export function getApiClient(): ApiClient {
  const container = (window as any).__DI_CONTAINER__;
  if (!container) {
    throw new Error('DI container not found. Make sure the app is initialized.');
  }
  return container.resolve('apiClient') as ApiClient;
}
